import React from "react";
import {
  FaAt,
  FaBuilding,
  FaCity,
  FaEnvelope,
  FaFacebook,
  FaGlobe,
  FaHashtag,
  FaHome,
  FaInstagram,
  FaMapMarkerAlt,
  FaPalette,
  FaPhone,
  FaSave,
  FaShieldAlt,
  FaUpload,
  FaUserTie,
} from "react-icons/fa";

function SectionCard({ eyebrow, title, icon, children, description }) {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="mb-5">
          <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            {eyebrow}
          </p>

          <h3 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
            <span className="text-indigo-600 dark:text-indigo-300">
              {icon}
            </span>
            {title}
          </h3>

          {description ? (
            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {description}
            </p>
          ) : null}
        </div>

        {children}
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, icon, type = "text", required, placeholder, hint, inputMode }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label} {required ? <span className="text-rose-500">*</span> : null}
      </label>

      <div className="relative">
        {icon ? (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            {icon}
          </span>
        ) : null}

        <input
          type={type}
          name={name}
          value={value || ""}
          onChange={onChange}
          required={required}
          placeholder={placeholder}
          inputMode={inputMode}
          className={`w-full rounded-2xl border border-slate-200 bg-white py-2.5 pr-3 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 ${
            icon ? "pl-10" : "pl-3"
          }`}
        />
      </div>

      {hint ? (
        <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function SkeletonForm() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="space-y-3 p-5">
        {[...Array(8)].map((_, index) => (
          <div
            key={index}
            className="h-12 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    </section>
  );
}

export default function AssociationForm({
  formId,
  settings,
  loading,
  saving,
  logoSrc,
  seasonOptions,
  onChange,
  onSubmit,
  onOpenLogoUpload,
}) {
  if (loading) return <SkeletonForm />;

  return (
    <form id={formId} onSubmit={onSubmit} className="space-y-5">
      <div className="grid gap-5 xl:grid-cols-[1fr,360px]">
        <SectionCard
          eyebrow="Association"
          title="Identité du club"
          icon={<FaBuilding />}
          description="Ces informations sont utilisées sur le site, les documents publics et les espaces d’inscription."
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <Field
                label="Nom du club"
                name="nom_club"
                value={settings.nom_club}
                onChange={onChange}
                icon={<FaBuilding />}
                required
                placeholder="Ex : Karaté Club Plaisirois"
              />
            </div>

            <Field
              label="Nom raccourci"
              name="shortname_club"
              value={settings.shortname_club}
              onChange={onChange}
              icon={<FaAt />}
              required
              placeholder="Ex : KCP78"
            />

            <Field
              label="Responsable du club"
              name="responsable_club"
              value={settings.responsable_club}
              onChange={onChange}
              icon={<FaUserTie />}
              placeholder="Ex : Nom Prénom"
            />

            <Field
              label="SIRET"
              name="siret_club"
              value={settings.siret_club}
              onChange={onChange}
              icon={<FaHashtag />}
              placeholder="Ex : 123 456 789 00012"
            />

            <Field
              label="Lien du logo"
              name="logo_club"
              value={settings.logo_club}
              onChange={onChange}
              icon={<FaUpload />}
              placeholder="https://... ou /uploads/..."
              hint="Vous pouvez coller une URL externe, un chemin /uploads/... ou téléverser le logo depuis la carte à droite."
            />
          </div>
        </SectionCard>

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="flex h-full flex-col items-center justify-center p-5 text-center">
            <div className="flex h-40 w-40 items-center justify-center overflow-hidden rounded-[32px] border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-950">
              <img
                src={logoSrc}
                alt="Logo du club"
                className="h-full w-full object-contain"
                onError={(event) => {
                  event.currentTarget.src = "/img/no-image.jpg";
                }}
              />
            </div>

            <h3 className="mt-4 text-lg font-bold text-slate-900 dark:text-white">
              Logo du club
            </h3>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Utilisé sur le site public, les documents, certains emails et
              l’espace adhérent.
            </p>

            <button
              type="button"
              onClick={onOpenLogoUpload}
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-[#012f6d] dark:bg-indigo-500 dark:hover:bg-indigo-400"
            >
              <FaUpload />
              Changer le logo
            </button>
          </div>
        </section>
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        <SectionCard
          eyebrow="Coordonnées"
          title="Adresse et contact"
          icon={<FaMapMarkerAlt />}
          description="Informations de contact principales du club."
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <Field
                label="Adresse"
                name="adresse_club"
                value={settings.adresse_club}
                onChange={onChange}
                icon={<FaHome />}
                placeholder="Rue et numéro"
              />
            </div>

            <Field
              label="Commune"
              name="commune_club"
              value={settings.commune_club}
              onChange={onChange}
              icon={<FaCity />}
              placeholder="Ex : Plaisir"
            />

            <Field
              label="Téléphone"
              name="tel_club"
              value={settings.tel_club}
              onChange={onChange}
              icon={<FaPhone />}
              type="tel"
              inputMode="tel"
              placeholder="Ex : 05 56 XX XX XX"
            />

            <div className="md:col-span-2">
              <Field
                label="Email"
                name="mail_club"
                value={settings.mail_club}
                onChange={onChange}
                icon={<FaEnvelope />}
                type="email"
                inputMode="email"
                placeholder="contact@club.fr"
              />
            </div>
          </div>
        </SectionCard>

        <SectionCard
          eyebrow="Web"
          title="Site et réseaux sociaux"
          icon={<FaGlobe />}
          description="Liens affichés sur les pages publiques du club."
        >
          <div className="grid gap-4">
            <Field
              label="Site web"
              name="siteweb_club"
              value={settings.siteweb_club}
              onChange={onChange}
              icon={<FaGlobe />}
              type="url"
              inputMode="url"
              placeholder="https://..."
            />

            <Field
              label="Facebook"
              name="facebook_club"
              value={settings.facebook_club}
              onChange={onChange}
              icon={<FaFacebook />}
              type="url"
              inputMode="url"
              placeholder="https://facebook.com/..."
            />

            <Field
              label="Instagram"
              name="instagram_club"
              value={settings.instagram_club}
              onChange={onChange}
              icon={<FaInstagram />}
              type="url"
              inputMode="url"
              placeholder="https://instagram.com/..."
            />
          </div>
        </SectionCard>
      </div>

      <SectionCard
        eyebrow="Configuration"
        title="Saison, couleur et renouvellement"
        icon={<FaPalette />}
        description="Paramètres globaux utilisés dans le site et le parcours adhérent."
      >
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/70">
            <label className="text-sm font-bold text-slate-700 dark:text-slate-200">
              Couleur primaire
            </label>

            <div className="mt-3 flex items-center gap-3">
              <input
                type="color"
                name="primary_color"
                value={settings.primary_color || "#4f46e5"}
                onChange={onChange}
                className="h-11 w-16 cursor-pointer rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900"
                aria-label="Couleur primaire"
              />

              <span
                className="inline-flex items-center justify-center rounded-2xl px-4 py-2 text-sm font-bold text-white shadow-sm"
                style={{
                  backgroundColor: settings.primary_color || "#4f46e5",
                }}
              >
                Aperçu
              </span>
            </div>

            <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
              Couleur utilisée pour personnaliser certains éléments publics.
            </p>
          </div>

          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/70">
            <label className="text-sm font-bold text-slate-700 dark:text-slate-200">
              Saison active
            </label>

            <select
              name="saison_active"
              value={settings.saison_active || ""}
              onChange={onChange}
              className="mt-3 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            >
              <option value="">Sélectionner une saison</option>
              {seasonOptions.map((season) => (
                <option key={season} value={season}>
                  {season}
                </option>
              ))}
            </select>

            <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
              Saison utilisée pour les inscriptions et les renouvellements.
            </p>
          </div>

          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/70">
            <div className="flex items-start gap-3">
              <span className="mt-1 text-indigo-600 dark:text-indigo-300">
                <FaShieldAlt />
              </span>

              <div className="min-w-0 flex-1">
                <label className="text-sm font-bold text-slate-700 dark:text-slate-200">
                  Renouvellement adhérent
                </label>

                <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                  Si activé, les adhérents peuvent renouveler leur inscription
                  en fin de saison depuis l’espace public.
                </p>
              </div>

              <input
                type="checkbox"
                name="autorisation_renouvellement"
                checked={!!settings.autorisation_renouvellement}
                onChange={onChange}
                className="toggle toggle-primary mt-1"
                aria-label="Autoriser le renouvellement"
              />
            </div>
          </div>
        </div>
      </SectionCard>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
        >
          <FaSave />
          {saving ? "Enregistrement..." : "Mettre à jour"}
        </button>
      </div>
    </form>
  );
}